// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The flipper — a genuinely driven rigid body, not an animation.
//
// This is where a pinball simulation is won or lost. Cradles, drop catches, live catches,
// post passes, tap passes and backhands are not features anyone writes: they are what
// emerges when the bat has real inertia, the coil has a real ramp-up, the return spring
// is genuinely weaker than the coil, and the ball genuinely pushes back.
//
// See 02 §8.

import {
  FLIPPER_COIL_RAMP_SECONDS,
  FLIPPER_EOS_ANGLE,
  FLIPPER_EOS_TORQUE_RATIO,
  FLIPPER_LENGTH,
  FLIPPER_RADIUS_PIVOT,
  FLIPPER_RADIUS_TIP,
  FLIPPER_RETURN_TORQUE,
  FLIPPER_STOP_RESTITUTION,
  FLIPPER_STRENGTH,
  FLIPPER_SWEEP,
} from './constants'
import { cos, sinCos } from '../math/deterministic'

/**
 * Conversion from the Visual-Pinball-scale `strength` a machine definition carries to SI
 * torque in newton-metres.
 *
 * Authors think in the familiar era scale — 500–1000 for electro-mechanical, 3200–3300
 * for mid-1990s solid state — while the simulation works in SI. The constant is fixed by
 * calibration against PH-A07: a full-strength flip on a stationary ball at the tip must
 * separate at 5.8–6.4 m/s.
 *
 * Calibrated 2026-08-16 at 0.5535. The first estimate of 0.72 N·m at strength 3250 measured
 * 6.96 m/s — too hot — and the square-root correction gave 0.554.
 *
 * **Re-calibrated 2026-08-19 to 7.664, and the factor of fourteen is the point.** The old
 * figure was fitted against a contact that manufactured energy: the bat was infinitely massive
 * in the impulse denominator and finitely massive when it took the recoil, so a fraction of a
 * newton-metre threw the ball at 6 m/s. With the denominator corrected (see `contact.ts`) the
 * same coil managed 2.36, and the coil now has to do the work the ball actually leaves with.
 *
 * That work is checkable rather than fitted. The ball rides the bat through the whole stroke
 * and separates at end of stroke, so the coil must supply `½·I_total·ω²` with
 * `I_total = I_bat + m_ball·r²` = 3.44e-4 kg·m² at ω = 113 rad/s — about 2.2 J over 0.9076 rad,
 * a **mean** torque near 2.4 N·m. The peak is higher than the mean because the 25 ms coil ramp
 * is longer than the 12 ms stroke, so the current never gets near full before the bat is on its
 * stop. 2.4 N·m mean against a 30.8 mm/rad plunger arm is about 78 N of plunger force, which is
 * an ordinary flipper coil. The old 0.55 N·m was 18 N, which is not one.
 *
 * Re-measure with `npm run calibrate` after any change here.
 */
export const VPX_STRENGTH_TO_TORQUE = 7.664 / FLIPPER_STRENGTH

/**
 * Bat mass, kg. Comparable to the ball, matching Visual Pinball's `mass = 1` normalisation.
 *
 * **Chosen by play, 17 Aug 2026.** Three candidates were built into the harness and cycled with
 * `F` under a live ball — 0.080 kg at strength 3250 (this one), 0.100 at 3406, and 0.108 at 3482
 * modelling bat plus crank, link and plunger reflected in through the linkage. Each strength was
 * swept empirically so that all three separate the ball at 6.10 m/s, which is the only way an
 * A/B tells you about *response* rather than about power. The bare bat won.
 *
 * The three strengths were 3250 / 3800 / 7700 until 19 Aug 2026, when the contact solver was
 * corrected and the spread turned out to be an artefact of it — see `src/harness/flipperTunings.ts`.
 *
 * The alternatives are kept in `src/harness/session.ts` rather than deleted, because the next
 * question about flipper feel is answered by feeling them again, not by re-deriving them.
 */
export const FLIPPER_BAT_MASS = 0.08

export interface FlipperConfig {
  readonly pivotX: number
  readonly pivotY: number
  /** Absolute bat direction at rest, radians from +X. */
  readonly restAngle: number
  /** `+1` rotates counter-clockwise to fire (a left flipper); `-1` clockwise (a right flipper). */
  readonly direction: 1 | -1
  readonly sweep?: number
  readonly length?: number
  readonly radiusPivot?: number
  readonly radiusTip?: number
  /** Visual-Pinball-scale coil strength. NEON NOVA uses 3250. */
  readonly strength?: number
  /** Return spring torque, N·m. Absolute: a spring is not a fraction of a solenoid. */
  readonly returnTorque?: number
  readonly coilRampSeconds?: number
  readonly eosAngle?: number
  readonly eosTorqueRatio?: number
  /**
   * Mass of the whole rotating assembly, kg — bat, crank, link and coil plunger together.
   *
   * Not just the bat, despite the name being about the part you can see. The plunger is a
   * steel slug travelling ~28 mm for the 52° sweep, so it reflects into the rotation as
   * `m·(0.028/0.908)²` and contributes about as much inertia as the bat itself. Getting this
   * wrong is invisible in the flip — the torque is calibrated against tip speed either way —
   * and highly visible in how the bat answers a ball that hits *it*.
   */
  readonly batMass?: number
}

export class Flipper {
  /** Explicitly layered bats only; -1 preserves historical unlayered contacts. */
  contactLayer = -1
  readonly pivotX: number
  readonly pivotY: number
  readonly restAngle: number
  readonly endAngle: number
  readonly direction: 1 | -1
  readonly length: number
  readonly radiusPivot: number
  readonly radiusTip: number

  // Derived dynamics. Mutable only through `retune`, never assigned from the step loop.
  coilTorque: number
  inertia: number
  invInertia: number
  batMass: number

  /** Return spring torque, N·m. Set once from the machine; the coil's calibration cannot move it. */
  readonly returnTorque: number
  readonly coilRampSeconds: number
  readonly eosAngle: number
  readonly eosTorqueRatio: number

  /** Current bat direction, radians. */
  angle: number
  /** Angle at the start of the current step, for render interpolation only. */
  prevAngle: number
  /** Angular velocity, rad/s. */
  omega = 0
  /** Button state. */
  pressed = false
  /**
   * The step the coil was last energised on, and last released on. −1 for neither.
   *
   * Written by the simulation, read by presentation — the same arrangement as
   * `PopBumper.firedAtStep` and for the same reason. A renderer or an audio layer polling
   * `pressed` once a frame cannot see a tap that begins and ends inside those sixteen
   * milliseconds, and a flipper is the sound a player hears most in the whole machine: one
   * missing click is one the ear notices precisely because the rest are always there.
   *
   * Both edges, because they are two different sounds. The coil energising is the stroke; the
   * coil letting go is a softer, lower thump as the bat drops back, and a flipper that makes
   * one noise per press is a button rather than a mechanism.
   */
  pressedAtStep = -1
  releasedAtStep = -1
  /** Seconds the button has been held, for the coil ramp. */
  private heldFor = 0

  /**
   * The bat's whole mutable state (M4-28a).
   *
   * `heldFor` is private and is the reason this lives on the class rather than beside the world
   * snapshot: it is how long the coil has been energised, which drives the ramp from initial to
   * full torque. A bat restored mid-stroke without it re-energises from cold and hits the ball
   * softer than the one that was saved — the same game, played differently, with nothing in the
   * data to show why.
   */
  snapshot(): FlipperState {
    return {
      angle: this.angle,
      prevAngle: this.prevAngle,
      omega: this.omega,
      pressed: this.pressed,
      pressedAtStep: this.pressedAtStep,
      releasedAtStep: this.releasedAtStep,
      heldFor: this.heldFor,
      tipX: this.tipX,
      tipY: this.tipY,
    }
  }

  restore(s: FlipperState): void {
    this.angle = s.angle
    this.prevAngle = s.prevAngle
    this.omega = s.omega
    this.pressed = s.pressed
    this.pressedAtStep = s.pressedAtStep
    this.releasedAtStep = s.releasedAtStep
    this.heldFor = s.heldFor
    this.tipX = s.tipX
    this.tipY = s.tipY
  }

  // Cached bat endpoints, refreshed once per step rather than per contact query.
  tipX = 0
  tipY = 0

  constructor(config: FlipperConfig) {
    this.pivotX = config.pivotX
    this.pivotY = config.pivotY
    this.restAngle = config.restAngle
    this.direction = config.direction
    this.length = config.length ?? FLIPPER_LENGTH
    this.radiusPivot = config.radiusPivot ?? FLIPPER_RADIUS_PIVOT
    this.radiusTip = config.radiusTip ?? FLIPPER_RADIUS_TIP

    const sweep = config.sweep ?? FLIPPER_SWEEP
    this.endAngle = this.restAngle + this.direction * sweep

    this.returnTorque = config.returnTorque ?? FLIPPER_RETURN_TORQUE
    this.coilRampSeconds = config.coilRampSeconds ?? FLIPPER_COIL_RAMP_SECONDS
    this.eosAngle = config.eosAngle ?? FLIPPER_EOS_ANGLE
    this.eosTorqueRatio = config.eosTorqueRatio ?? FLIPPER_EOS_TORQUE_RATIO

    this.batMass = config.batMass ?? FLIPPER_BAT_MASS
    this.coilTorque = 0
    this.inertia = 0
    this.invInertia = 0
    this.retune(config.strength ?? FLIPPER_STRENGTH, this.batMass)

    this.angle = this.restAngle
    this.prevAngle = this.restAngle
    this.refreshGeometry()
  }

  /**
   * Set the coil strength and the rotating mass, recomputing everything derived from them.
   *
   * A machine adjustment, and it exists because the two cannot sensibly be chosen apart. Tip
   * speed goes as `√(2τθ/I)` with `I = mL²/3`, so **holding PH-A07's 5.8–6.4 m/s means scaling
   * the torque with the mass**: change one alone and the calibration moves. Everything that
   * distinguishes a light bat from a heavy one — how it answers a struck ball, how forgiving a
   * live catch is, how much a drop catch deadens — lives in the ratio the flip itself hides.
   *
   * **Not for use mid-replay.** A validated replay re-simulates from the machine definition,
   * so its bats are whatever the definition says; retuning a live session is a tuning control
   * and makes that session's trace unreproducible, which is fine for an instrument and not
   * fine for a score.
   */
  retune(strength: number, batMass: number): void {
    this.batMass = batMass
    this.coilTorque = strength * VPX_STRENGTH_TO_TORQUE
    // The return spring is **not** touched. It used to be recomputed here as a fraction of the
    // coil, which meant every A/B of the coil was silently also an A/B of the spring — see
    // `FLIPPER_RETURN_TORQUE`. Retuning the coil is a statement about the coil.
    // A bat pivoting about one end: I = (1/3)·m·L².
    this.inertia = (batMass * this.length * this.length) / 3
    this.invInertia = 1 / this.inertia
  }

  /**
   * Press or release the button. Input is sampled per simulation step, never per frame.
   *
   * `atStep` is the caller's step index and is only ever recorded — nothing in the simulation
   * reads it back, so a caller that has no step counter (a calibration rig driving one bat in
   * isolation) can leave it out and change nothing about the physics.
   */
  setPressed(pressed: boolean, atStep = -1): void {
    if (pressed === this.pressed) return
    if (pressed) {
      this.heldFor = 0
      this.pressedAtStep = atStep
    } else {
      this.releasedAtStep = atStep
    }
    this.pressed = pressed
  }

  /** Fraction of the sweep travelled, 0 at rest and 1 at end of stroke. */
  strokeFraction(): number {
    const travelled = (this.angle - this.restAngle) * this.direction
    const total = (this.endAngle - this.restAngle) * this.direction
    const f = travelled / total
    return f < 0 ? 0 : f > 1 ? 1 : f
  }

  /** Advance the bat by one simulation step. */
  step(dt: number): void {
    this.prevAngle = this.angle
    let torque: number

    if (this.pressed) {
      this.heldFor += dt

      // The coil's magnetic field takes time to build. This is exactly what makes a light
      // tap possible — a very short press moves the bat a little way at low torque — and
      // therefore what makes a tap pass a skill rather than an accident.
      const ramp =
        this.coilRampSeconds <= 0 ? 1 : Math.min(1, this.heldFor / this.coilRampSeconds)

      // Within the end-of-stroke angle the coil switches to its hold winding: strong
      // enough to resist a struck ball, not so strong that the bat is infinitely rigid.
      const remaining = (this.endAngle - this.angle) * this.direction
      const holding = remaining <= this.eosAngle
      const scale = holding ? this.eosTorqueRatio : 1

      torque = this.direction * this.coilTorque * ramp * scale
    } else {
      this.heldFor = 0
      // The return spring is two orders of magnitude weaker than the coil, and that is what
      // lets a ball be dropped gently off a flipper rather than punted. It is stated in N·m
      // rather than as a fraction, because it is a spring and the coil is a solenoid.
      torque = -this.direction * this.returnTorque
    }

    this.omega += torque * this.invInertia * dt
    this.angle += this.omega * dt

    this.clampToStops(torque * this.invInertia * dt)
    this.refreshGeometry()
  }

  /**
   * Mechanical stops at rest and at full extension.
   *
   * The bat *bounces* off its stop rather than freezing there. A flipper that stops dead
   * at maximum extension reads as fake immediately (PH-A10).
   */
  private clampToStops(deltaOmega: number): void {
    const past = (this.angle - this.endAngle) * this.direction
    if (past > 0) {
      this.angle = this.endAngle
      if (this.omega * this.direction > 0) this.omega = this.bounceOffStop(deltaOmega)
      return
    }
    const before = (this.restAngle - this.angle) * this.direction
    if (before > 0) {
      this.angle = this.restAngle
      if (this.omega * this.direction < 0) this.omega = this.bounceOffStop(deltaOmega)
    }
  }

  /**
   * Reverse the bat off a stop, or absorb it entirely if it is barely moving.
   *
   * The absorb case matters: without it the torque holding the bat against the stop and the
   * elastic stop itself settle into a permanent limit cycle, and the bat buzzes against its
   * stop forever — visibly and audibly.
   *
   * **The threshold is derived from the torque doing the holding, not read from a constant
   * (19 Aug 2026).** It used to be `FLIPPER_STOP_REST_OMEGA = 1.5` rad/s, and the constants file
   * justified it by working the limit cycle out and then writing the answer down. Writing the
   * answer down is what broke: the working is a function of `τ`, and `τ` is not a constant.
   * Re-calibrating the coil multiplied the end-of-stroke torque by fourteen, the limit cycle
   * with it, and the bat buzzed against its end stop at 1.75 m/s of tip speed for as long as
   * the button was held.
   *
   * The cycle is worth deriving here rather than quoting. Sitting on the stop, each step adds
   * `Δω` into it and the stop returns `−e·Δω`; the next step adds `Δω` again, so the arrival
   * speeds run `Δω, Δω(1−e), Δω(1−e+e²), …` and converge on `Δω/(1+e)`. The rebound that has
   * to be absorbed is `e` times that — for the shipped hold torque, 4.13 rad/s, which is
   * exactly what the buzzing bat measured. Doubling it is the headroom.
   *
   * `deltaOmega` is what this step's torque added, so one expression now covers the end stop,
   * the rest stop, any coil, any return spring and any rotating mass — and a rebound the ball
   * or the coil genuinely earned still survives it (PH-A10).
   */
  private bounceOffStop(deltaOmega: number): number {
    const reversed = -this.omega * FLIPPER_STOP_RESTITUTION
    const e = FLIPPER_STOP_RESTITUTION
    const limitCycle = (Math.abs(deltaOmega) * e) / (1 + e)
    return Math.abs(reversed) < limitCycle * 2 ? 0 : reversed
  }

  private refreshGeometry(): void {
    const { sin, cos } = sinCos(this.angle)
    this.tipX = this.pivotX + cos * this.length
    this.tipY = this.pivotY + sin * this.length
  }

  /** Surface velocity at a point on the bat: `ω × r`, in the playfield plane. */
  surfaceVelocityAt(px: number, py: number, out: { x: number; y: number }): void {
    const rx = px - this.pivotX
    const ry = py - this.pivotY
    out.x = -this.omega * ry
    out.y = this.omega * rx
  }

  /**
   * Reaction from a ball contact.
   *
   * The ball genuinely pushes the bat back, so a fast shot striking a flipper mid-stroke
   * measurably slows it. Without this, coil strength is a number rather than a feel.
   */
  applyBallReaction(px: number, py: number, impulseX: number, impulseY: number): void {
    const rx = px - this.pivotX
    const ry = py - this.pivotY
    // Equal and opposite: the ball received (impulseX, impulseY).
    const torqueZ = rx * -impulseY - ry * -impulseX
    this.omega += torqueZ * this.invInertia
  }

  /**
   * Validate the two-flipper geometry constraints of PH-054.
   *
   * A ball caught between two interpenetrating bats is ejected violently and reads
   * instantly as a bug — this exact trap consumed most of the prototype design effort
   * recorded in `ai_docs/specs-and-notes/notes.md`. Encoding it as a check is how that
   * lesson is banked; the Machine Builder surfaces the same numbers live.
   */
  static validatePair(
    pivotSeparationHalf: number,
    length: number,
    restAngleBelowHorizontal: number,
    tipRadius: number,
    ballRadius: number,
  ): { interpenetrates: boolean; centreDrainGap: number; hasCentreDrain: boolean } {
    const reach = length * cos(restAngleBelowHorizontal)
    const interpenetrates = reach >= pivotSeparationHalf - tipRadius
    const gap = 2 * (pivotSeparationHalf - reach) - 2 * tipRadius
    return {
      interpenetrates,
      centreDrainGap: gap,
      hasCentreDrain: gap >= 2 * ballRadius,
    }
  }
}

/** A flipper's whole mutable state, for a world snapshot (M4-28a). */
export interface FlipperState {
  readonly angle: number
  readonly prevAngle: number
  readonly omega: number
  readonly pressed: boolean
  readonly pressedAtStep: number
  readonly releasedAtStep: number
  /** Seconds the coil has been energised. Drives the torque ramp; private to the bat. */
  readonly heldFor: number
  readonly tipX: number
  readonly tipY: number
}
